/**
 * The shared visual language for "in plain words" explainers.
 *
 * DESIGN LINEAGE (owner feedback, 2026-08-24, twice). The first cut was blue-tinted slabs of
 * prose; the second was a plain white box with a grey micro-label — "old and tired" next to the
 * rest of the app. This version speaks the app's OWN design language, taken from the components
 * the owner already likes:
 *   • headings use GradientText — the same blue→teal treatment as "Financial Impact";
 *   • the surface is the ImpactMetric family: white, rounded-2xl, border-slate-200/70,
 *     shadow-card;
 *   • before→after movements follow ImpactMetric's exact grammar: muted before, arrow, big bold
 *     after, then a signed delta coloured by direction;
 *   • allocations are LEDGER rows (label left, money right, tabular) — the format of the
 *     downsize "Where every dollar goes" table the owner called the better one. Chips are kept
 *     ONLY for flows (Sell → RAD → DAP on the Suggestion Engine cards), where sequence is the
 *     information.
 */
import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ArrowRight, ChevronDown, ChevronRight, CornerDownRight } from 'lucide-react';
import { GradientText } from '../ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';

export function PlainWordsCard({
    eyebrow,
    children,
    className = '',
    collapsible = false,
    defaultOpen = true,
    beatLayout = 'boxes',
    headingStyle = 'gradient',
}: {
    /**
     * The card's heading — rendered in the app's gradient heading style.
     *
     * Pass null to render the card without one. That is for the case where the surface AROUND the
     * card already names it: the Aged Care recap now opens inside "Understanding your plan", and a
     * heading there would repeat the modal's title and the plan summary panel's beside it.
     */
    eyebrow?: string | null;
    children: React.ReactNode;
    className?: string;
    /**
     * Let the reader fold this away once they have read it.
     *
     * For cards that state a POSITION rather than a result — the walk-in note is read once at the
     * start and then sits between the room price and every control below it for the rest of the
     * session (owner, 2026-08-26). Folding is per-visit state, not saved: it always opens the first
     * time, because the position is the thing every strategy below is measured against.
     */
    collapsible?: boolean;
    defaultOpen?: boolean;
    /**
     * How the beats inside are drawn (UX review, 17 Oct 2026). 'boxes' is the dotted heading over
     * its own bordered box; 'rows' is the mock-up's table — the heading in a left column, the words
     * beside it, one rule between rows — for a card that states a position rather than a result.
     */
    beatLayout?: BeatLayout;
    /** The heading's colour: the app's gradient, or plain navy (the mock-up's headings). */
    headingStyle?: 'gradient' | 'navy';
}) {
    const [open, setOpen] = useState(defaultOpen);
    const body = open || !collapsible ? <BeatLayoutContext.Provider value={beatLayout}>{children}</BeatLayoutContext.Provider> : null;
    const heading = headingStyle === 'navy' ? <span className="text-brand-navy">{eyebrow}</span> : <GradientText>{eyebrow}</GradientText>;
    return (
        <div className={`rounded-2xl border border-slate-200/70 bg-white p-5 shadow-card ${className}`}>
            {eyebrow && (
                collapsible ? (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        className={`flex w-full items-center gap-2 text-left ${open ? 'mb-3' : ''}`}
                    >
                        <h4 className="text-lg font-bold">
                            {heading}
                        </h4>
                        <ChevronDown
                            size={18}
                            className={`ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                        />
                        <span className="sr-only">{open ? 'Hide' : 'Show'}</span>
                    </button>
                ) : (
                    <h4 className="mb-3 text-lg font-bold">
                        {heading}
                    </h4>
                )
            )}
            {body}
        </div>
    );
}

export type BeatLayout = 'boxes' | 'rows';
/** Set by PlainWordsCard; read by every PlainWordsBeat inside it, however deep. */
const BeatLayoutContext = React.createContext<BeatLayout>('boxes');

export type BeatTone = 'neutral' | 'info' | 'good' | 'warn' | 'bad';

/**
 * One beat of a plain-words explanation: a dotted heading, then the prose in its own bordered box.
 *
 * The design the owner picked out of the layout mock-up (2026-08-26 — "you can use some of the
 * design principles from Understanding your plan to make this look much more professional"). It
 * replaces the pattern these cards used before, which was three or four bolded run-in labels
 * ("Care:", "Age Pension:", "The gap:") inside one block of prose — readable, but nothing to scan,
 * so an adviser hunting for the pension line had to read the lot.
 *
 * The dot and the box rail carry the beat's MEANING, and the tones are not decorative:
 *   neutral  a plain statement of fact
 *   info     an entitlement or a rule
 *   good/bad a movement in a direction
 *   warn     something to look at — kept scarce, so it still means that when it appears
 *
 * The heading words say the same thing on their own, so nothing is lost to a reader who cannot
 * separate the colours.
 */
const BEAT_TONES: Record<BeatTone, { dot: string; rail: string }> = {
    neutral: { dot: 'bg-slate-400', rail: 'border-l-slate-300' },
    info: { dot: 'bg-blue-500', rail: 'border-l-blue-500' },
    good: { dot: 'bg-emerald-500', rail: 'border-l-emerald-500' },
    warn: { dot: 'bg-amber-500', rail: 'border-l-amber-500' },
    bad: { dot: 'bg-rose-500', rail: 'border-l-rose-500' },
};

export function PlainWordsBeat({
    heading,
    tone = 'neutral',
    children,
}: {
    heading: React.ReactNode;
    tone?: BeatTone;
    children: React.ReactNode;
}) {
    const { dot, rail } = BEAT_TONES[tone];
    const layout = React.useContext(BeatLayoutContext);
    if (layout === 'rows') {
        return (
            <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3.5 first:pt-0 last:border-0 last:pb-0 sm:grid-cols-[250px_1fr] sm:gap-6" data-beat-tone={tone}>
                {/* Balanced wrapping, so a heading never leaves one word on its own line (owner, 18 Oct 2026: "what sets / it"). */}
                <p className="text-[14px] font-bold leading-snug text-brand-navy [text-wrap:balance]">{heading}</p>
                <div className="text-[14px] leading-relaxed text-slate-700">{children}</div>
            </div>
        );
    }
    return (
        <div className="py-2.5 first:pt-0">
            <div className="flex items-center gap-2.5">
                <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${dot}`} aria-hidden="true" />
                <span className="text-[15px] font-bold tracking-tight text-blue-600">{heading}</span>
                <span className="h-px flex-1 bg-slate-200 " aria-hidden="true" />
            </div>
            <div
                className={`ml-[17px] mt-2 rounded-lg border border-slate-200 border-l-[3px] ${rail} bg-white px-3.5 py-3 text-[13.5px] leading-relaxed text-slate-600`}
            >
                {children}
            </div>
        </div>
    );
}

/**
 * The consequence of ONE entry, shown directly beneath the field that caused it.
 *
 * WHY THIS SHAPE. The app already had this idea in one place and nowhere else: enter a share sale to
 * fund a deposit, and the capital gains tax appears under that asset, at the moment it becomes true
 * (DisposalCgtEstimatePanel). Owner direction, 2026-08-28 — do that everywhere. A consequence
 * belongs where the decision is made, not collected into a floating panel the reader has to go and
 * find, and certainly not both.
 *
 * WHEN TO USE IT, and when not to:
 *   • USE for something that becomes true because of what was just typed into the field above — the
 *     gift that will still be counted for five years, the part of a funeral bond above the exempt
 *     limit, the interest that compounds on a loan nobody is repaying.
 *   • DO NOT use for what the whole plan does to the client's position. That is the plan summary's
 *     job, and repeating it here is how the old insight panels came to say everything twice.
 *
 * The tone vocabulary is deliberately the same as {@link PlainWordsBeat} — warn still means "look at
 * this" and stays scarce — so a note under a field and a beat in an explanation read as one product.
 * `heading` carries the meaning in words, so nothing depends on the colour alone.
 */
export function FieldNote({
    heading,
    tone = 'info',
    children,
}: {
    heading?: React.ReactNode;
    tone?: BeatTone;
    children: React.ReactNode;
}) {
    const { rail } = BEAT_TONES[tone];
    const headingTone: Record<BeatTone, string> = {
        neutral: 'text-slate-700',
        info: 'text-blue-700',
        good: 'text-emerald-700',
        warn: 'text-amber-800',
        bad: 'text-rose-700',
    };
    return (
        <div
            className={`mt-2 rounded-lg border border-slate-200 border-l-[3px] ${rail} bg-slate-50/70 px-3 py-2.5 text-[13px] leading-relaxed text-slate-600`}
        >
            {heading && <p className={`font-semibold ${headingTone[tone]} mb-1`}>{heading}</p>}
            {children}
        </div>
    );
}

export type MoneyFlowTone = 'sold' | 'good' | 'warn' | 'neutral';

export interface MoneyFlowStep {
    amount: number;
    label: string;
    /** Tiny second line inside the chip — what this money means ("refundable", "like rent"). */
    sub?: string;
    tone?: MoneyFlowTone;
}

/*
 * White chips, colour as accent only (owner, 2026-08-24: pastel infills "look old"). The app's
 * own stat tiles are white surfaces whose colour lives in text and icons - chips follow suit:
 * a coloured dot and coloured sub-text carry the meaning, the box stays white.
 */
const CHIP_TONES: Record<MoneyFlowTone, { dot: string; sub: string }> = {
    sold: { dot: 'bg-slate-400', sub: 'text-slate-500' },
    good: { dot: 'bg-emerald-500', sub: 'text-emerald-700' },
    warn: { dot: 'bg-amber-500', sub: 'text-amber-700' },
    neutral: { dot: 'bg-slate-300', sub: 'text-slate-500' },
};

/** A money story as chips with arrows (Sell → RAD → DAP) — for FLOWS, where sequence matters. */
export function MoneyFlow({ steps }: { steps: MoneyFlowStep[] }) {
    return (
        <div className="flex flex-wrap items-center gap-y-2">
            {steps.map((step, i) => {
                const tone = CHIP_TONES[step.tone || 'neutral'];
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <ChevronRight size={16} className="mx-1 shrink-0 text-slate-300" aria-hidden="true" />}
                        <span className="inline-flex flex-col rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 leading-tight shadow-sm">
                            <span className="flex items-center gap-1.5 text-sm font-bold tabular-nums text-slate-900">
                                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} aria-hidden="true" />
                                {formatCurrency(step.amount)}
                                <span className="text-xs font-medium text-slate-500">{step.label}</span>
                            </span>
                            {step.sub && <span className={`pl-3 text-[11px] font-medium ${tone.sub}`}>{step.sub}</span>}
                        </span>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/** Means-test meaning as a tag — same vocabulary as the downsize ledger's chips. */
export function MeaningChip({ tone = 'good', children }: { tone?: 'good' | 'warn'; children: React.ReactNode }) {
    return (
        <span
            className={`ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide ${
                tone === 'good' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
            }`}
        >
            {children}
        </span>
    );
}

/**
 * One line of a money ledger: label (with optional meaning chip) left, amount right, tabular.
 * The format of the downsize "Where every dollar goes" table — the presentation the owner kept.
 */
export function LedgerRow({
    label,
    amount,
    negative = false,
    bold = false,
    muted = false,
    indent = false,
    divider = false,
    chip,
    chipTone = 'good',
}: {
    label: React.ReactNode;
    amount: number;
    /** Render as an outflow (−$X). */
    negative?: boolean;
    bold?: boolean;
    /** De-emphasised row (e.g. selling costs). */
    muted?: boolean;
    /** A destination under a source — indented with a corner arrow. */
    indent?: boolean;
    /** Rule above the row (totals). */
    divider?: boolean;
    chip?: React.ReactNode;
    chipTone?: 'good' | 'warn';
}) {
    return (
        <div
            className={`flex items-baseline justify-between gap-3 py-1 ${divider ? 'mt-1 border-t border-slate-200 pt-1.5' : ''}`}
        >
            <span
                className={`flex min-w-0 items-baseline text-sm ${bold ? 'font-bold text-slate-900' : muted ? 'text-slate-500' : 'text-slate-700'} ${indent ? 'pl-4' : ''}`}
            >
                {indent && <CornerDownRight size={12} className="mr-1.5 shrink-0 self-center text-slate-300" aria-hidden="true" />}
                <span className="min-w-0">{label}</span>
                {chip && <MeaningChip tone={chipTone}>{chip}</MeaningChip>}
            </span>
            <span
                className={`shrink-0 text-sm tabular-nums ${bold ? 'font-bold text-slate-900' : muted ? 'text-slate-500' : 'font-semibold text-slate-800'}`}
            >
                {negative ? `−${formatCurrency(amount)}` : formatCurrency(amount)}
            </span>
        </div>
    );
}

/**
 * A before → after movement in ImpactMetric's own grammar: uppercase label, muted before, arrow,
 * big bold after, signed delta coloured by direction — plus room for a "(why?)" link.
 */
/**
 * One driver of a movement — "what moves it" — with its amount where the model has one.
 * `suffix` is for a yearly figure ("a year"); sign decides the colour and the +/−.
 */
export interface EstateDriver {
    text: string;
    amount?: number;
    suffix?: string;
    sign: 'plus' | 'minus' | 'neutral';
}

/**
 * "What moves it", as a list (owner, 24 Sep 2026: "very text heavy with one big paragraph … why
 * wouldn't we try and tidy this up"). One lead sentence, one line per driver with the amount
 * beside it — red for what leaves the estate, green for what grows it, grey for a swap — and one
 * closing line. Used inside a ShiftRow's `why` on every module's "Understanding your plan".
 */
export function WhatMovesIt({ lead, drivers, closing }: { lead: React.ReactNode; drivers: EstateDriver[]; closing?: React.ReactNode }) {
    const tone = (sign: EstateDriver['sign']) =>
        sign === 'plus' ? { dot: 'bg-emerald-500', text: 'text-emerald-700', mark: '+' } : sign === 'minus' ? { dot: 'bg-rose-400', text: 'text-rose-700', mark: '−' } : { dot: 'bg-slate-400', text: 'text-slate-700', mark: '' };
    return (
        <>
            <p>{lead}</p>
            {drivers.length > 0 && (
                <>
                    <p className="mt-2 font-semibold text-slate-800">What moves it</p>
                    <ul className="mt-1 space-y-1">
                        {drivers.map((d, i) => {
                            const t = tone(d.sign);
                            return (
                                <li key={i} className="flex items-start gap-2">
                                    <span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${t.dot}`} aria-hidden="true" />
                                    {/* The amount follows the sentence, not a column of its own: a right-hand column
                                        made the text wrap around it and left a ragged gap mid-sentence (owner, 25 Sep 2026). */}
                                    <span>
                                        {d.text}
                                        {d.amount != null && Number.isFinite(d.amount) && (
                                            <span className={`ml-2 whitespace-nowrap font-semibold tabular-nums ${t.text}`}>
                                                {t.mark}{formatCurrency(Math.abs(d.amount))}{d.suffix ? <span className="ml-1 text-[11px] font-medium text-slate-500">{d.suffix}</span> : null}
                                            </span>
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
            {closing && <p className="mt-2 text-slate-600">{closing}</p>}
        </>
    );
}

export function ShiftRow({
    label,
    before,
    after,
    suffix = '/yr',
    preferLower = false,
    children,
    variant = 'inline',
    why,
}: {
    label: string;
    before: number;
    after: number;
    /** Appended to the bold after-figure (default "/yr"). */
    suffix?: string;
    /** Falling is the GOOD direction for this figure (a care contribution, a fee) - flips the delta colours. */
    preferLower?: boolean;
    children?: React.ReactNode;
    /**
     * 'inline' (default) is the compact row that sits in a card on the page: label, figures, and
     * whatever `children` the caller supplies — normally a tappable "(why?)".
     *
     * 'expanded' is for a surface with room to explain properly: a headline naming the movement in
     * words, and the reason ALWAYS VISIBLE in a bordered box beneath rather than hidden behind a
     * tap. Used by "Understanding your plan", where the whole point is the explanation and hiding it
     * behind three separate taps wastes the space the modal exists to provide.
     */
    variant?: 'inline' | 'expanded';
    /** The reason, for `variant="expanded"`. Rendered as prose in a box, not behind a tooltip. */
    why?: React.ReactNode;
}) {
    const difference = after - before;
    const isFlat = Math.round(difference) === 0;
    const isPositive = difference >= 0;
    const isGood = preferLower ? !isPositive : isPositive;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const deltaColour = isFlat ? 'text-slate-400' : isGood ? 'text-emerald-600' : 'text-rose-600';

    if (variant === 'expanded') {
        /*
         * The dot encodes DIRECTION, matching the green/red the impact rows already use — and
         * neutral is SLATE, never amber. Amber means "check this" elsewhere in the app, and "no
         * change" is not a warning: on the Age Pension row it is the most reassuring line on the
         * page ("paying a RAD does not cost you pension"). The heading words carry the meaning on
         * their own, so nothing is lost to a reader who cannot separate red from green.
         */
        const dot = isFlat ? 'bg-slate-400' : isGood ? 'bg-emerald-500' : 'bg-rose-500';
        const rail = isFlat ? 'border-l-slate-400' : isGood ? 'border-l-emerald-500' : 'border-l-rose-500';
        const movement = isFlat
            ? 'no change'
            : `${isGood ? 'better' : 'worse'} by ${formatCurrency(Math.abs(difference))}`;
        return (
            <div className="py-3 first:pt-0">
                <div className="flex items-center gap-2.5">
                    <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${dot}`} aria-hidden="true" />
                    <span className="text-[15px] font-bold tracking-tight text-blue-600">
                        {label} — {movement}
                    </span>
                    <span className="h-px flex-1 bg-slate-200 " aria-hidden="true" />
                </div>
                <div className="ml-[17px] mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm tabular-nums text-slate-400">{formatCurrency(before)}</span>
                    <ArrowRight size={13} className="shrink-0 self-center text-slate-300" aria-hidden="true" />
                    <span className="text-lg font-bold tabular-nums text-slate-900">
                        {formatCurrency(after)}
                        <span className="ml-0.5 text-xs font-medium text-slate-500">{suffix}</span>
                    </span>
                    <span className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${deltaColour}`}>
                        {!isFlat && <Icon size={14} className="shrink-0" aria-hidden="true" />}
                        {isFlat ? 'no change' : `${isPositive ? '+' : '−'}${formatCurrency(Math.abs(difference))}`}
                    </span>
                </div>
                {why && (
                    <div
                        className={`ml-[17px] mt-2 rounded-lg border border-slate-200 border-l-[3px] ${rail} bg-white px-3.5 py-3 text-[13.5px] leading-relaxed text-slate-600`}
                    >
                        {why}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="py-2 first:pt-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm text-slate-400 tabular-nums">{formatCurrency(before)}</span>
                <ArrowRight size={13} className="shrink-0 self-center text-slate-300" aria-hidden="true" />
                <span className="text-lg font-bold text-slate-900 tabular-nums">
                    {formatCurrency(after)}
                    <span className="ml-0.5 text-xs font-medium text-slate-500">{suffix}</span>
                </span>
                <span className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${deltaColour}`}>
                    {!isFlat && <Icon size={14} className="shrink-0" aria-hidden="true" />}
                    {isFlat ? 'no change' : `${isPositive ? '+' : '−'}${formatCurrency(Math.abs(difference))}`}
                </span>
                {children && <span className="text-sm">{children}</span>}
            </div>
        </div>
    );
}
